// components/TableManager.jsx
import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    Grid,
    Card,
    CardContent,
    Typography,
    IconButton,
    Box,
    Chip,
    Button,
    CircularProgress,
    Alert
} from '@mui/material';
import { Close, Restaurant, QrCodeScanner, People, Star } from '@mui/icons-material';
import api from '../services/api';

const TableManager = ({ open, onClose, onSelectTable, currentTableId, language }) => {
    const [tables, setTables] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    useEffect(() => {
        if (open) {
            fetchTables();
        }
    }, [open]);

    const fetchTables = async () => {
        setLoading(true);
        setError('');
        try {
            const response = await api.get('/admin/cafe-tables');
            // Handle both response formats
            const tablesData = response.data.tables || response.data;
            setTables(Array.isArray(tablesData) ? tablesData : []);
        } catch (error) {
            console.error('Error fetching tables:', error);
            setError(language === 'ar' ? 'خطأ في تحميل الطاولات' : 'Error loading tables');
        } finally {
            setLoading(false);
        }
    };

    const getStatusColor = (table) => {
        // Check if table is occupied
        if (table.is_occupied === true) return '#FF3D00';
        // Check status field
        return '#4CAF50';
    };

    const getStatusText = (table) => {
        // Check if table is occupied
        if (table.is_occupied === true) return language === 'ar' ? 'مشغولة' : 'Occupied';
        // Check status field
        return language === 'ar' ? 'متاحة' : 'Available';
    };

    const isTableDisabled = (table) => {
        // Don't disable if it's the current table
        if (table.id === currentTableId) return true;
        // Disable if occupied
        if (table.is_occupied === true) return true;
        // Disable if status is not available
        return table.status !== 'available' && table.status !== undefined;
    };

    const handleTableClick = (table) => {
        if (!isTableDisabled(table) && table.id !== currentTableId) {
            onSelectTable(table);
            onClose();
        }
    };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="md"
            fullWidth
            PaperProps={{
                sx: {
                    backgroundColor: '#0D0D0D',
                    borderRadius: '24px',
                    border: '1px solid #FFD700',
                    maxHeight: '90vh'
                }
            }}
        >
            <DialogTitle sx={{ 
                borderBottom: '1px solid #2D2D2D',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
            }}>
                <Typography variant="h5" sx={{ color: '#FFD700', fontWeight: 'bold' }}>
                    {language === 'ar' ? 'اختر الطاولة' : 'Select Table'}
                </Typography>
                <IconButton onClick={onClose} sx={{ color: '#B3B3B3' }}>
                    <Close />
                </IconButton>
            </DialogTitle>

            <DialogContent sx={{ p: 3 }}>
                {error && (
                    <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>
                        {error}
                    </Alert>
                )}

                {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                        <CircularProgress sx={{ color: '#FFD700' }} />
                    </Box>
                ) : tables.length === 0 ? (
                    <Box sx={{ textAlign: 'center', py: 4 }}>
                        <Typography sx={{ color: '#B3B3B3' }}>
                            {language === 'ar' ? 'لا توجد طاولات' : 'No tables available'}
                        </Typography>
                    </Box>
                ) : (
                    <>
                        {/* Current Table Info */}
                        {currentTableId && (
                            <Box sx={{ 
                                mb: 3, 
                                p: 2, 
                                bgcolor: '#1A1A1A', 
                                borderRadius: '12px',
                                border: '1px solid #FFD700'
                            }}>
                                <Typography variant="body2" sx={{ color: '#B3B3B3', mb: 1 }}>
                                    {language === 'ar' ? 'الطاولة الحالية' : 'Current Table'}
                                </Typography>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                    <Restaurant sx={{ color: '#FFD700' }} />
                                    <Typography variant="h6" sx={{ color: '#FFD700', fontWeight: 'bold' }}>
                                        {language === 'ar' ? `طاولة رقم ${currentTableId}` : `Table ${currentTableId}`}
                                    </Typography>
                                </Box>
                            </Box>
                        )}

                        {/* Tables Grid */}
                        <Grid container spacing={2}>
                            {tables.map((table) => {
                                const disabled = isTableDisabled(table);
                                const isOccupied = table.is_occupied === true;
                                const isCurrent = table.id === currentTableId;
                                const isVip = table.is_vip === true;
                                const statusColor = getStatusColor(table);
                                
                                return (
                                    <Grid item xs={6} sm={4} md={3} key={table.id}>
                                        <Card
                                            sx={{
                                                cursor: disabled && !isCurrent ? 'not-allowed' : 'pointer',
                                                opacity: disabled && !isCurrent ? 0.5 : 1,
                                                backgroundColor: isCurrent ? '#2D2D2D' : '#1A1A1A',
                                                border: isCurrent ? '2px solid #FFD700' : '1px solid #2D2D2D',
                                                transition: 'transform 0.2s, box-shadow 0.2s',
                                                position: 'relative',
                                                '&:hover': {
                                                    transform: (!disabled || isCurrent) ? 'translateY(-4px)' : 'none',
                                                    boxShadow: (!disabled || isCurrent) ? '0 8px 24px rgba(255, 215, 0, 0.2)' : 'none',
                                                    borderColor: (!disabled || isCurrent) ? '#FFD700' : '#2D2D2D'
                                                }
                                            }}
                                            onClick={() => handleTableClick(table)}
                                        >
                                            <CardContent sx={{ textAlign: 'center', p: 2 }}>
                                                {/* Current Badge */}
                                                {isCurrent && (
                                                    <Chip
                                                        label={language === 'ar' ? 'حالي' : 'CURRENT'}
                                                        size="small"
                                                        sx={{
                                                            position: 'absolute',
                                                            top: 8,
                                                            right: 8,
                                                            backgroundColor: '#FFD700',
                                                            color: '#000000',
                                                            fontWeight: 'bold',
                                                            fontSize: '10px'
                                                        }}
                                                    />
                                                )}
                                                
                                                {/* Occupied Badge */}
                                                {isOccupied && !isCurrent && (
                                                    <Chip
                                                        label={language === 'ar' ? 'مشغولة' : 'OCCUPIED'}
                                                        size="small"
                                                        sx={{
                                                            position: 'absolute',
                                                            top: 8,
                                                            right: 8,
                                                            backgroundColor: '#FF3D00',
                                                            color: '#FFF',
                                                            fontWeight: 'bold',
                                                            fontSize: '10px'
                                                        }}
                                                    />
                                                )}
                                                
                                                {/* VIP Badge */}
                                                {isVip && (
                                                    <Chip
                                                        icon={<Star sx={{ fontSize: 12 }} />}
                                                        label="VIP"
                                                        size="small"
                                                        sx={{
                                                            position: 'absolute',
                                                            top: 8,
                                                            left: 8,
                                                            backgroundColor: '#FFD700',
                                                            color: '#000000',
                                                            fontWeight: 'bold',
                                                            fontSize: '10px'
                                                        }}
                                                    />
                                                )}
                                                
                                                <Restaurant sx={{ fontSize: 40, color: '#FFD700', mb: 1 }} />
                                                <Typography variant="h6" sx={{ color: '#FFF', fontWeight: 'bold' }}>
                                                    {table.table_name || (language === 'ar' ? `طاولة ${table.table_number}` : `Table ${table.table_number}`)}
                                                </Typography>
                                                
                                                {/* Capacity */}
                                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5, mt: 0.5 }}>
                                                    <People sx={{ fontSize: 14, color: '#B3B3B3' }} />
                                                    <Typography variant="caption" sx={{ color: '#B3B3B3' }}>
                                                        {table.capacity || 4} {language === 'ar' ? 'أشخاص' : 'persons'}
                                                    </Typography>
                                                </Box>
                                                
                                                {/* Location */}
                                                {table.location && (
                                                    <Typography variant="caption" sx={{ color: '#B3B3B3', display: 'block', mt: 0.5 }}>
                                                        {table.location}
                                                    </Typography>
                                                )}
                                                
                                                {/* Status Chip */}
                                                {!isCurrent && (
                                                    <Chip
                                                        label={getStatusText(table)}
                                                        size="small"
                                                        sx={{
                                                            mt: 1,
                                                            backgroundColor: statusColor,
                                                            color: '#FFF',
                                                            fontWeight: 'bold'
                                                        }}
                                                    />
                                                )}
                                            </CardContent>
                                        </Card>
                                    </Grid>
                                );
                            })}
                        </Grid>

                        {/* Legend */}
                        <Box sx={{ mt: 3, display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Box sx={{ width: 12, height: 12, borderRadius: '50%', backgroundColor: '#4CAF50' }} />
                                <Typography variant="caption" sx={{ color: '#B3B3B3' }}>
                                    {language === 'ar' ? 'متاحة' : 'Available'}
                                </Typography>
                            </Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Box sx={{ width: 12, height: 12, borderRadius: '50%', backgroundColor: '#FF3D00' }} />
                                <Typography variant="caption" sx={{ color: '#B3B3B3' }}>
                                    {language === 'ar' ? 'مشغولة' : 'Occupied'}
                                </Typography>
                            </Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Box sx={{ width: 12, height: 12, borderRadius: '50%', backgroundColor: '#FF9800' }} />
                                <Typography variant="caption" sx={{ color: '#B3B3B3' }}>
                                    {language === 'ar' ? 'محجوزة' : 'Reserved'}
                                </Typography>
                            </Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Box sx={{ width: 24, height: 12, borderRadius: '2px', backgroundColor: '#2D2D2D', border: '2px solid #FFD700' }} />
                                <Typography variant="caption" sx={{ color: '#B3B3B3' }}>
                                    {language === 'ar' ? 'حالية' : 'Current'}
                                </Typography>
                            </Box>
                            {tables.some(t => t.is_vip) && (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Star sx={{ fontSize: 12, color: '#FFD700' }} />
                                    <Typography variant="caption" sx={{ color: '#B3B3B3' }}>
                                        VIP
                                    </Typography>
                                </Box>
                            )}
                        </Box>
                    </>
                )}
            </DialogContent>
        </Dialog>
    );
};

export default TableManager;